import {
  DataSet,
  RegExpMatcher,
  englishDataset,
  englishRecommendedTransformers,
  parseRawPattern,
} from 'obscenity';
import {
  CUSTOM_PROFANITY_EXACT_TERMS,
  CUSTOM_PROFANITY_STEMS,
} from '@/lib/customProfanityTerms';

const escapePatternLiteral = (value) => String(value || '').replace(/[\\?[\]|]/g, '\\$&');

const addCustomStem = (dataset, term) => dataset.addPhrase((phrase) => phrase
  .setMetadata({ originalWord: term })
  .addPattern(parseRawPattern(`|${escapePatternLiteral(term)}`)));

const addCustomExactTerm = (dataset, term) => dataset.addPhrase((phrase) => phrase
  .setMetadata({ originalWord: term })
  .addPattern(parseRawPattern(`|${escapePatternLiteral(term)}|`)));

// Stems match a whole-word start and allow suffixes, while exact terms match
// only the whole word.
const customProfanityDataset = CUSTOM_PROFANITY_EXACT_TERMS.reduce(
  (dataset, term) => addCustomExactTerm(dataset, term),
  CUSTOM_PROFANITY_STEMS.reduce(
    (dataset, term) => addCustomStem(dataset, term),
    new DataSet(),
  ),
);

const customProfanityMatcher = new RegExpMatcher({
  ...customProfanityDataset.build(),
  ...englishRecommendedTransformers,
});

const defaultProfanityMatcher = new RegExpMatcher({
  ...englishDataset.build(),
  ...englishRecommendedTransformers,
});

export const findCustomBlockedWord = (value) => {
  const text = String(value || '').trim();
  if (!text) return null;
  const matches = customProfanityMatcher.getAllMatches(text, true);
  if (!matches.length) return null;
  return customProfanityDataset.getPayloadWithPhraseMetadata(matches[0])?.phraseMetadata?.originalWord || null;
};

export const findCustomBlockedDetail = (value) => {
  const text = String(value || '').trim();
  if (!text) return null;
  const matches = customProfanityMatcher.getAllMatches(text, true);
  if (!matches.length) return null;
  const match = customProfanityDataset.getPayloadWithPhraseMetadata(matches[0]);
  const startIndex = Number(match?.startIndex);
  const endIndex = Number(match?.endIndex);
  let matchedText = null;
  if (Number.isFinite(startIndex) && Number.isFinite(endIndex) && endIndex >= startIndex) {
    let wordStart = startIndex;
    let wordEnd = endIndex + 1;
    while (wordStart > 0 && !/\s/.test(text[wordStart - 1])) {
      wordStart -= 1;
    }
    while (wordEnd < text.length && !/\s/.test(text[wordEnd])) {
      wordEnd += 1;
    }
    matchedText = text.slice(wordStart, wordEnd) || null;
  }
  return {
    blockedWord: match?.phraseMetadata?.originalWord || null,
    matchedText,
  };
};

export const hasCustomProfanity = (value) => findCustomBlockedWord(value) !== null;

export const hasDefaultProfanity = (value) => {
  const text = String(value || '').trim();
  if (!text) return false;
  return defaultProfanityMatcher.hasMatch(text);
};
